<template>
  <div
    v-if="visible"
    class="confirm-fill"
    :style="{ '--p': progress }"
  >
    <mi-btn
      class="confirm-fill__btn"
      :disable="disabled"
      type="button"
      @click="confirmAction"
    >
      <span class="confirm-fill__text">
        <slot name="button" :seconds="seconds">
          Confirm ({{ seconds }} sec)
        </slot>
      </span>
    </mi-btn>

    <q-popup-proxy
      v-model="internalVisible"
      anchor="top middle"
      self="bottom middle"
      :offset="[0, 8]"
      persistent
      no-parent-event
      :breakpoint="0"
    >
      <div class="loading-tooltip">
        <slot name="tooltip">
          Processing may take longer<br />
          while the system replaces product models
        </slot>
      </div>
    </q-popup-proxy>
  </div>
</template>

<script>
  import { ref, computed, watch, onBeforeUnmount } from 'vue'

  export default {
    name: 'MiProgressBtn',
    props: {
      visible: {
        type: Boolean,
        required: true
      },
      durationMs: {
        type: Number,
        default: 10000
      },
      disabled: {
        type: Boolean,
        default: false
      }
    },
    emits: ['confirm', 'cancel'],
    setup(props, { emit }) {
      const progress = ref(0)
      const internalVisible = ref(false)

      let rafId = null
      let startTime = 0

      const seconds = computed(() => {
        const total = props.durationMs / 1000
        const elapsed = Math.floor(progress.value * total)
        return Math.max(0, total - elapsed)
      })

      const start = () => {
        progress.value = 0
        startTime = performance.now()

        const tick = now => {
          const elapsed = now - startTime
          progress.value = Math.min(elapsed / props.durationMs, 1)

          if (elapsed < props.durationMs) rafId = requestAnimationFrame(tick)
          else rafId = null
        }

        rafId = requestAnimationFrame(tick)
      }

      const reset = () => {
        if (rafId) cancelAnimationFrame(rafId)
        rafId = null
        progress.value = 0
        emit('cancel')
      }

      watch(
        () => props.visible,
        val => {
          internalVisible.value = val
          if (val) start()
          else reset()
        },
        { immediate: true }
      )

      onBeforeUnmount(() => {
        if (rafId) cancelAnimationFrame(rafId)
      })

      const confirmAction = () => {
        emit('cancel')
        emit('confirm')
      }

      return {
        progress,
        seconds,
        internalVisible,
        confirmAction
      }
    }
  }
</script>

<style lang="scss" scoped>
  /* stylelint-disable declaration-no-important */
  .confirm-fill {
    position: relative;
    display: inline-block;
    border-radius: 4px;
    overflow: hidden;
    background: #6889ac;
  }

  .confirm-fill:before {
    content: "";
    position: absolute;
    inset: 0;
    width: calc(var(--p) * 100%);
    background: $mi-anthracite-800;
    z-index: 0;
    transition: width .1s linear;
    pointer-events: none;
  }

  .confirm-fill__btn {
    position: relative;
    z-index: 1;
    background: transparent !important;
  }

  .confirm-fill__text {
    position: relative;
    z-index: 2;
    color: $mi-white;
  }

  .loading-tooltip {
    background: $mi-anthracite-800;
    color: $mi-white;
    padding: 12px 16px;
    font-size: 14px;
    line-height: 1.4;
    max-width: 260px;
    text-align: center;
  }
</style>
